import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Typography,
  TextField,
  Paper,
  Autocomplete,
  CircularProgress,
  Collapse,
  Divider,
} from '@mui/material';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import { UnlinkedBatch } from '../../../types/batch';
import type { CreateBatchFormData } from './validation';

interface BatchSelectionSectionProps {
  /** The react-hook-form control object */
  control: Control<CreateBatchFormData>;
  /** The form field errors */
  errors: FieldErrors<CreateBatchFormData>;
  /** List of available (unlinked) batches */
  batches: UnlinkedBatch[];
  /** Whether batches are currently loading */
  loading: boolean;
  /** Whether the form is being submitted */
  disabled: boolean;
  /** Whether this section is visible (with-batch mode is active) */
  visible: boolean;
  /** Whether the batch field is required */
  required?: boolean;
}

/**
 * BatchSelectionSection Component
 *
 * An autocomplete section for selecting an existing batch to link
 * to a new workflow. Used in "with-batch" workflow mode.
 */
export const BatchSelectionSection: React.FC<BatchSelectionSectionProps> = ({
  control,
  errors,
  batches,
  loading,
  disabled,
  visible,
  required = true,
}) => {
  const selectedBatchId = useWatch({ control, name: 'batchId' });

  const selectedBatch = useMemo(
    () => batches.find((b) => b.id === selectedBatchId) || null,
    [batches, selectedBatchId]
  );

  return (
    <Collapse in={visible}>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          Batch Selection
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select an existing batch to create a workflow for tracking its processing.
        </Typography>
        <Controller
          name="batchId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              value={batches.find((b) => b.id === field.value) || null}
              onChange={(_, newValue) => field.onChange(newValue?.id || null)}
              options={batches}
              getOptionLabel={(option) =>
                option.batchNumber || `Batch #${option.id}`
              }
              loading={loading}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Batch"
                  required={required}
                  error={!!errors.batchId}
                  helperText={
                    errors.batchId?.message as string ||
                    'Select an existing batch to create a workflow'
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key: _key, ...rest } = props;
                return (
                  <li key={option.id} {...rest}>
                    <Box>
                      <Typography variant="body2">
                        {option.batchNumber || `Batch #${option.id}`}
                      </Typography>
                      {option.farm?.farmName && (
                        <Typography variant="caption" color="text.secondary">
                          Farm: {option.farm.farmName}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
            />
          )}
        />

        {/* Batch Preview Panel */}
        <Collapse in={!!selectedBatch}>
          {selectedBatch && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Batch Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                {selectedBatch.farmer && (
                  <Typography variant="body2" color="text.secondary">
                    Farmer: {selectedBatch.farmer.firstName} {selectedBatch.farmer.surname || ''}
                  </Typography>
                )}
                {selectedBatch.farm && (
                  <Typography variant="body2" color="text.secondary">
                    Farm: {selectedBatch.farm.farmName}
                  </Typography>
                )}
                {selectedBatch.lab && (
                  <Typography variant="body2" color="text.secondary">
                    Lab: {selectedBatch.lab.labName}
                  </Typography>
                )}
                {selectedBatch.clientRef && (
                  <Typography variant="body2" color="text.secondary">
                    Client Ref: {selectedBatch.clientRef}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Created: {new Date(selectedBatch.dateCreated).toLocaleDateString()}
                </Typography>
              </Box>
              {selectedBatch.sampleCount > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Samples: ${selectedBatch.sampleCount}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                  {selectedBatch.resultTypes?.map((type) => (
                    <Chip
                      key={type}
                      label={type}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Collapse>
      </Paper>
    </Collapse>
  );
};

export default BatchSelectionSection;
